import { MkIcon } from "@/mk/MkIcon";
import { useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";

import { MkAvatar } from "./MkAvatar";
import { MkImageModal } from "./MkImageModal";

interface Props {
    value?: string | false;
    src?: string | false;
    hasImage?: boolean;
    name?: string;
    title?: string;
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    square?: boolean;
    disabled?: boolean;
    onUpload?(dataUrl: string): void;
    onClear?(): void;
}

export function MkAvatarEditor({ value, src, hasImage = false, name, title, size = "xl",
                                 square = false, disabled, onUpload, onClear }: Props) {
    const { t } = useTranslation();
    const input = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);

    const previewSrc = typeof value === "string" && value.startsWith("data:")
        ? value
        : (hasImage && src ? src : "");
    const modalTitle = title || t("mk.avatar.image");

    function pick() {
        if (!disabled) {
            input.current?.click();
        }
    }

    function onFile(event: ChangeEvent<HTMLInputElement>) {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) {
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            onUpload?.(String(reader.result));
            setOpen(false);
        };
        reader.readAsDataURL(file);
    }

    return (
        <div className={`mk-avatar-editor${disabled ? " is-disabled" : ""}`}>
            <button type="button" className="mk-avatar-editor-btn" title={t("mk.avatar.change")}
                    disabled={disabled} onClick={() => {
                        if (!disabled) {
                            setOpen(true);
                        }
                    }}>
                <MkAvatar size={size} square={square} src={previewSrc || null} name={name || ""} />
                {!disabled && (
                    <span className="mk-avatar-editor-badge">
                        <MkIcon name="fa-camera" />
                    </span>
                )}
            </button>

            <input type="file" accept="image/*" className="mk-avatar-editor-input"
                   ref={input} onChange={onFile} />

            <MkImageModal open={open} title={modalTitle} previewSrc={previewSrc}
                          disabled={disabled} onClose={() => setOpen(false)}
                          onPick={pick} onClear={onClear} />
        </div>
    );
}
